import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

import { City } from '../../domain/city/entities/city.entity';
import { Types } from '../../domain/types.domain';

import { AppHandler } from '../app.handler';
import {
  decideTenantResolution,
  emitTenantResolutionEvents,
} from './tenant-resolution';

const getCityFromPath = (path: string): string => {
  const segments = path.split('?')[0].split('/').filter(Boolean);
  const firstSegment = segments[0] ?? '';
  const normalized = firstSegment.toLowerCase();

  if (normalized === 'accountconfirmation' || normalized === 'account-confirmation') {
    return '';
  }

  return firstSegment;
};

const getCityFromLocation = (): string => {
  if (typeof window === 'undefined') return '';
  const segments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] ?? '';
  const normalized = firstSegment.toLowerCase();

  if (normalized === 'accountconfirmation' || normalized === 'account-confirmation') {
    return '';
  }

  return firstSegment;
};

/**
 * APR-2964/2965 — resolução de tenant em duas fases.
 *
 * SHADOW (default): o citiesWhitelist hardcoded segue decidindo; a API
 * (cities/identity + auth-config.connection) é consultada em paralelo e as
 * divergências viram telemetria. Comportamento do usuário INALTERADO.
 *
 * DINÂMICO (settings.dynamicTenantResolution = true): a API decide, a lista
 * vira fallback com alarme, e identidade desconhecida é FAIL-CLOSED (nada de
 * redirect mudo). A decisão em si é pura — ver tenant-resolution.ts.
 */
export const cityResolver: ResolveFn<City | null> = async (route, state) => {
  const router = inject(Router);
  const _appHandler = inject(AppHandler);

  const _settings = inject(Types.Settings);
  const getCityByIdentity = inject(Types.GetCityByIdentityService);
  // optional: rota sem o provider degrada para a lista (shadow sem connection),
  // NUNCA derruba a tela de login com NullInjectorError.
  const authRepository = inject(Types.AuthRepository, { optional: true });

  const citySubdomain =
    route.paramMap.get('city') ||
    getCityFromPath(state.url) ||
    getCityFromLocation();

  const fromCityWhitelist = _settings.citiesWhitelist.find(
    (it) => it.identity === citySubdomain
  );

  // A cidade é resolvida pela API SEMPRE (antes o fetch só acontecia depois do
  // gate da lista — o que tornava o shadow impossível).
  //
  // O AppHandler vem de `route.providers`, e o injetor da rota sobrevive entre
  // navegações: o cache guarda a cidade ANTERIOR. Sem conferir a identidade
  // contra a URL, trocar de cidade dentro do SPA (o select-city navega pelo
  // Router) mantinha a cidade antiga inteira — logo, título e a connection de
  // autenticação — sob a URL da cidade nova.
  const cachedCity = _appHandler.getCity();
  let city = cachedCity?.identidade === citySubdomain ? cachedCity : null;

  if (!city && citySubdomain) {
    try {
      city = await getCityByIdentity.handle({ identity: citySubdomain });
    } catch {
      city = null; // API fora: a decisão abaixo degrada para a lista
    }
  }

  // connection dinâmica (APR-2963) — best-effort, nunca derruba o login.
  let apiConnection: string | undefined;
  if (authRepository && city?.index !== undefined && city?.index !== null) {
    try {
      const config = await authRepository.getConfigByCityId(city.index);
      apiConnection = (config as { connection?: string }).connection;
    } catch {
      apiConnection = undefined;
    }
  }

  const decision = decideTenantResolution({
    identity: citySubdomain,
    whitelistEntry: fromCityWhitelist,
    apiCity: city ? { index: city.index, identidade: (city as any).identidade } : null,
    apiConnection,
    dynamicMode: (_settings as { dynamicTenantResolution?: boolean }).dynamicTenantResolution === true,
  });

  emitTenantResolutionEvents(decision);

  if (!decision.allow || !city) {
    router.navigate(['/']);
    return null;
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem('city', citySubdomain);
  }

  _appHandler.setCity(city);

  return Object.assign(city, {
    connection: decision.connection,
    loginUrl: decision.loginUrl,
  });
};
